import * as React from 'react';
import clsx from 'clsx';
import { XMarkIcon, LinkIcon } from '@heroicons/react/24/outline';
import type { TrainingLink } from '@/types/echo_creation';

// Import Social Icons
import youtubeIcon from '@/assets/images/svg/youtube.svg';
import linkedinIcon from '@/assets/images/svg/linkedin.svg';
import twitterIcon from '@/assets/images/svg/twitter.svg';
import instagramIcon from '@/assets/images/svg/instagram.svg';
import facebookIcon from '@/assets/images/svg/facebook.svg';

interface LinkListItemProps {
  link: TrainingLink;
  onDelete: (id: string) => void;
}

export const LinkListItem: React.FC<LinkListItemProps> = ({ link, onDelete }) => {
  const getIcon = (url: string) => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return youtubeIcon;
    if (lowerUrl.includes('linkedin.com')) return linkedinIcon;
    if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return twitterIcon;
    if (lowerUrl.includes('instagram.com')) return instagramIcon;
    if (lowerUrl.includes('facebook.com')) return facebookIcon;
    return null; // Fallback to Heroicon
  };

  const iconSrc = getIcon(link.url);

  return (
    <div
      className={clsx(
        'flex w-full items-center',
        'bg-white/60 backdrop-blur-sm', // #FFFFFF99
        'rounded-20', // radius 20px
        'gap-3 px-4 py-3',
        'transition-all duration-200'
      )}
    >
      {/* Social Icon or Generic Link Icon */}
      <div className='flex size-8 shrink-0 items-center justify-center'>
        {iconSrc ? (
          <img src={iconSrc} alt='Social' className='size-full object-contain' />
        ) : (
          <div className='flex size-8 items-center justify-center rounded-full bg-gray-200'>
            <LinkIcon className='size-5 text-gray-500' />
          </div>
        )}
      </div>

      {/* Green Dot (6px x 6px, #6EDF21 -> accent-300) */}
      <div className='size-[6px] shrink-0 rounded-full bg-accent-300' />

      {/* Link URL (Truncated) */}
      <div className='min-w-0 flex-1'>
        <span className='block truncate font-sans text-16 font-medium leading-27 text-dark-300'>
          {link.url}
        </span>
      </div>

      {/* Delete Button (Red circle background) */}
      <button
        onClick={() => onDelete(link.id)}
        className='flex size-8 shrink-0 items-center justify-center rounded-full bg-red-100 transition-colors hover:bg-red-200'
        aria-label='Remove link'
      >
        <XMarkIcon className='size-5 text-red-500' strokeWidth={2} />
      </button>
    </div>
  );
};
